import { Box } from '@chakra-ui/react';
import React from 'react';

import IconSvg from 'ui/shared/IconSvg';

import { CONTRACT_METHOD_FIELD_INPUT_HEIGHT } from './contractMethodFieldStyles';
import type { useContractMethodFieldStyles } from './contractMethodFieldStyles';

type FieldStyles = ReturnType<typeof useContractMethodFieldStyles>;

type IconName = 'address-array-minus' | 'address-array-plus' | 'address-batch-paste';

function getIconButtonBackground(
  isActive: boolean,
  bordered: boolean | undefined,
  styles: FieldStyles,
  variant: 'default' | 'hover',
): string {
  if (isActive) {
    return styles.brandColor;
  }

  if (bordered) {
    return variant === 'hover' ? styles.actionButtonHoverBg : styles.actionButtonBg;
  }

  return 'transparent';
}

interface Props {
  label: string;
  iconName: IconName;
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  isDisabled: boolean;
  styles: FieldStyles;
  bordered?: boolean;
  dataIndex?: number;
  active?: boolean;
}

const ContractMethodAddressArrayIconButton = ({
  label,
  iconName,
  onClick,
  isDisabled,
  styles,
  bordered,
  dataIndex,
  active,
}: Props) => {
  const isActive = Boolean(active);
  const iconColor = isActive ? styles.brandButtonText : styles.iconColor;
  const buttonSize = bordered ? CONTRACT_METHOD_FIELD_INPUT_HEIGHT : '16px';
  const buttonHeight = CONTRACT_METHOD_FIELD_INPUT_HEIGHT;
  const buttonBg = getIconButtonBackground(isActive, bordered, styles, 'default');
  const buttonHoverBg = getIconButtonBackground(isActive, bordered, styles, 'hover');
  const hoverOpacity = isActive ? 1 : 0.85;

  return (
    <Box
      as="button"
      type="button"
      aria-label={ label }
      data-index={ dataIndex }
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={ 0 }
      w={ buttonSize }
      h={ buttonHeight }
      minW={ buttonSize }
      color={ iconColor }
      bg={ buttonBg }
      border={ bordered ? '1px solid' : 'none' }
      borderColor={ bordered ? styles.actionButtonBorder : 'transparent' }
      borderRadius={ bordered ? '4px' : 'full' }
      cursor={ isDisabled ? 'not-allowed' : 'pointer' }
      opacity={ isDisabled ? 0.4 : 1 }
      onClick={ onClick }
      disabled={ isDisabled }
      _hover={ isDisabled ? undefined : {
        bg: buttonHoverBg,
        opacity: hoverOpacity,
      } }
      _focus={{ outline: 'none' }}
    >
      <IconSvg name={ iconName } boxSize={ 4 } color={ iconColor }/>
    </Box>
  );
};

export default React.memo(ContractMethodAddressArrayIconButton);
